import { cloneElement, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import {
  Box,
  Button,
  Heading,
  Label,
  LabeledControl,
  Paragraph,
  Radio,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import PaymentActions from '../../../actions/PaymentActions';
import ListingActivationTerms from '../../../components/ListingActivationTerms';
import SpinnerContainer from '../../../components/SpinnerContainer';
import PropertyConstants from '../../../constants/PropertyConstants';
import { PAYMENT_CONSTANTS } from '../../../constants/PaymentConstants';

import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

import type { FreemiumProduct } from '../../Freemium/interfaces';

const { PAYMENT_METHOD } = PAYMENT_CONSTANTS;

type Props = {
  creditCardCapture: JSX.Element;
  displaySelectPaymentMethodModal: boolean;
  isRenderedInPageView: boolean;
  onPaymentMethodChange: (method: string) => void;
  setCurrentPhase: (phase: string) => void;
};

const SelectPaymentMethod = ({
  creditCardCapture,
  displaySelectPaymentMethodModal = false,
  isRenderedInPageView = false,
  onPaymentMethodChange,
  setCurrentPhase,
}: Props) => {
  const dispatch = useDispatch();

  const { propertyId: fallbackListingAlias } = useParams<{
    propertyId: string;
  }>();

  const creditCardCaptureRef = useRef<HTMLDivElement>(null);

  const {
    payment: {
      capture: { isFormLoaded: isIframeLoaded },
      chargeAndActivateListing: { defaultPaymentMethodSuccess, defaultPaymentMethod: defaultCreditCard },
    },
    properties: {
      currentProperty: {
        listingAlias,
        freemium: { isEnabled: isFreemiumEnabled, products: freemiumProducts },
      },
    },
    app: {
      appConfig: {
        config: {
          features: { metroidIframeButtonText },
        },
      },
    },
  } = useSelector((state) => state);

  const [paymentMethod, setPaymentMethod] = useState(
    defaultPaymentMethodSuccess ? PAYMENT_METHOD.DEFAULT : PAYMENT_METHOD.NEW,
  );

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const shouldPerformScrollEffect =
      creditCardCaptureRef.current && isIframeLoaded && paymentMethod === PAYMENT_METHOD.NEW;
    let timerId: NodeJS.Timeout | null = null;

    if (shouldPerformScrollEffect) {
      // class is set in EditListingPage
      // dialog body is set via constellation Modal component
      // eslint-disable-next-line curly-quotes/no-straight-quotes
      const modalBodyQuery = '[data-c11n-component="Modal.Body"]';

      // if SelectPaymentMethod is being rendered for mobile web view, scroll at the window level.
      const ref: Window | Element | null = isRenderedInPageView ? window : document.querySelector(modalBodyQuery);

      const creditCardCaptureOffsetTop = creditCardCaptureRef.current.offsetTop - 40;

      if (ref) {
        timerId = setInterval(() => {
          const scrollHeight = 'scrollHeight' in ref ? ref.scrollHeight : ref.document.documentElement.scrollHeight;
          if (scrollHeight > 1000) {
            timerId && clearInterval(timerId);
            ref.scrollTo({
              top: creditCardCaptureOffsetTop,
              behavior: 'smooth',
            });

            timerId = null;
          }
        }, 500);
      }
    }
    return () => {
      timerId && clearInterval(timerId);
    };
  }, [isIframeLoaded, paymentMethod, isRenderedInPageView]);

  const selectedProduct = freemiumProducts.find(
    (product: FreemiumProduct) => product.type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
  );

  const onRefreshDefaultPaymentMethodClick = () => {
    setShowSpinner(true);

    dispatch(PaymentActions.getDefaultPaymentMethod(listingAlias || fallbackListingAlias))
      // @ts-expect-error
      .then(() => {
        setShowSpinner(false);
      });
  };

  const { maskedNumber, cardBrand } = defaultCreditCard || {};
  const isDefaultPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.DEFAULT;
  const isNewPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.NEW;

  const renderPricingContent = () => {
    if (isFreemiumEnabled && selectedProduct) {
      return (
        <Text>
          Your card will be charged a
          <strong data-type={selectedProduct?.type} data-test="display-price">
            {` one-time fee of ${selectedProduct?.price}. `}
          </strong>
          By purchasing this upgrade, you will have premium benefits for up to {selectedProduct?.duration}. Deactivate
          your listing when you find a renter.
        </Text>
      );
    }
  };

  const renderPaymentMethods = () => (
    <>
      <Box
        css={{
          padding: '400',
          backgroundColor: isDefaultPaymentMethodSelected ? '#fafafa' : undefined,
        }}
        className="fs-mask"
      >
        <LabeledControl
          disabled={!defaultPaymentMethodSuccess}
          label={
            <Label>
              {defaultPaymentMethodSuccess ? (
                <Text>
                  {maskedNumber}
                  <Text textStyle="body-bold" css={{ marginLeft: '400' }}>
                    {cardBrand}
                  </Text>
                </Text>
              ) : (
                'Default payment method'
              )}
            </Label>
          }
          control={
            <Radio
              data-test="default-payment-method-radio"
              name={PAYMENT_METHOD.DEFAULT}
              onChange={() => {
                setPaymentMethod(PAYMENT_METHOD.DEFAULT);
                onPaymentMethodChange(PAYMENT_METHOD.DEFAULT);
              }}
              checked={isDefaultPaymentMethodSelected}
            />
          }
        />
        {!defaultPaymentMethodSuccess ? (
          <Box css={{ paddingBlock: '400' }}>
            <Paragraph css={{ marginBottom: '400' }}>
              There was an error retrieving your default payment method.
            </Paragraph>
            <TextButton onClick={onRefreshDefaultPaymentMethodClick}>Refresh</TextButton>
          </Box>
        ) : (
          <div />
        )}
        {showSpinner && <SpinnerContainer />}
      </Box>
      <Box
        css={{
          padding: '400',
          backgroundColor: isNewPaymentMethodSelected ? '#fafafa' : undefined,
        }}
      >
        <LabeledControl
          label={<Label>Replace with new card</Label>}
          control={
            <Radio
              data-test="new-payment-method-radio"
              name={PAYMENT_METHOD.NEW}
              onChange={() => {
                setPaymentMethod(PAYMENT_METHOD.NEW);
                onPaymentMethodChange(PAYMENT_METHOD.NEW);
              }}
              checked={isNewPaymentMethodSelected}
            />
          }
        />
        {isNewPaymentMethodSelected &&
          // select new payment method
          // step: capture credit card
          cloneElement(creditCardCapture, {
            ref: creditCardCaptureRef,
          })}
      </Box>
    </>
  );

  if (!displaySelectPaymentMethodModal) {
    return creditCardCapture;
  }

  return (
    <div className="SelectPaymentMethod">
      <Heading level={1} textStyle="heading-sm">
        Checkout
      </Heading>
      <Paragraph css={{ paddingBlock: '400' }}>{renderPricingContent()}</Paragraph>
      <Paragraph textStyle="body-lg-bold" css={{ paddingBottom: '400' }}>
        Payment method
      </Paragraph>
      {renderPaymentMethods()}
      {isDefaultPaymentMethodSelected && (
        <div data-testid="ModalActions">
          <ListingActivationTerms
            actionText={`By clicking ${metroidIframeButtonText}`}
            isNotFlatFee={true}
            useFreemiumValues={isFreemiumEnabled}
            displayPrice={selectedProduct?.price}
          />

          <Button
            data-testid="checkout-button"
            tone="brand"
            emphasis="filled"
            fluid
            css={{ paddingBlock: '400', marginTop: '200' }}
            onClick={() => setCurrentPhase(PAYMENT_CONSTANTS.PAYMENT_PHASE.CREDIT_CARD_CHARGE)}
            data-eventid={ModularListingCSEvents.CompleteUpgradeToPremium.data_event_id}
          >
            {metroidIframeButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SelectPaymentMethod;
